'use client'

import { useEffect } from 'react'
import { useTheme } from 'next-themes'
import { getCalApi } from '@calcom/embed-react'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'

/**
 * The Cal.com booking action, in one place.
 *
 * This used to live inside app/schedule-meeting/page.tsx, so any other page
 * that wanted to offer a conversation had to link somewhere instead — and the
 * ones that did pointed at /about-us/team, a "Coming Soon" placeholder. A
 * shared button lets a CTA open the real booking modal in place rather than
 * sending someone to a dead end.
 *
 * The button carries `data-cal-*` attributes that the Cal embed script binds
 * to; the hook below is what loads that script and themes the modal, so a
 * button rendered without it would do nothing on click.
 */

export const CAL_NAMESPACE = 'appointment'
export const CAL_LINK = 'euclideum-solutions/appointment'
export const CAL_CONFIG =
  '{"layout":"month_view","useSlotsViewOnSmallScreen":true}'

/**
 * Loads the Cal embed and keeps the modal on the site's palette. Safe to call
 * from several components on one page — the embed API is idempotent per
 * namespace.
 */
export function useCalEmbed() {
  const { theme, resolvedTheme } = useTheme()

  useEffect(() => {
    // The embed was once pinned to dark, which dropped a black modal over the
    // light site. Follow whatever the page actually resolved to.
    const mode = (resolvedTheme ?? theme) === 'dark' ? 'dark' : 'light'

    ;(async () => {
      const cal = await getCalApi({ namespace: CAL_NAMESPACE })
      cal('ui', {
        theme: mode,
        styles: { branding: { brandColor: '#37ecb0' } },
        hideEventTypeDetails: false,
        layout: 'month_view',
      })
    })()
  }, [theme, resolvedTheme])
}

interface BookMeetingButtonProps
  extends React.ComponentPropsWithoutRef<typeof Button> {
  /** Set false when a parent already calls useCalEmbed(). */
  initEmbed?: boolean
  wrapperClassName?: string
}

export function BookMeetingButton({
  className,
  wrapperClassName,
  children = 'Schedule a Meeting',
  initEmbed = true,
  ...props
}: BookMeetingButtonProps) {
  return (
    <>
      {initEmbed ? <CalEmbedLoader /> : null}

      <Button
        type="button"
        data-cal-namespace={CAL_NAMESPACE}
        data-cal-link={CAL_LINK}
        data-cal-config={CAL_CONFIG}
        className={cn(wrapperClassName, className)}
        {...props}
      >
        {children}
      </Button>
    </>
  )
}

/** Renders nothing; exists so the hook can run without a wrapper element. */
function CalEmbedLoader() {
  useCalEmbed()
  return null
}
